import React, { useState } from "react";
import { Heading, Box, Card, Button } from "theme-ui";

import {
  Decimal,
  Decimalish,
  StabilityDeposit,
  LiquityStoreState,
  Difference
} from "@liquity/lib-base";

import { useLiquitySelector } from "@liquity/lib-react";

import { COIN } from "../../strings";

import { Icon } from "../Icon";
import { EditableRow, StaticRow } from "../Trove/Editor";
import { LoadingOverlay } from "../LoadingOverlay";
import BalanceLabel from "../BalanceLabel/BalanceLabel";
import useChain from "../../hooks/useChain";

import useSPRewardsAPR from "../../hooks/useSPRewardsAPR";

const select = ({ usdsBalance, usdsInStabilityPool }: LiquityStoreState) => ({
  usdsBalance,
  usdsInStabilityPool
});

type StabilityDepositEditorProps = {
  originalDeposit: StabilityDeposit;
  editedUSDS: Decimal;
  changePending: boolean;
  children?: React.ReactNode;
  dispatch: (action: { type: "setDeposit"; newValue: Decimalish } | { type: "revert" }) => void;
};

export const StabilityDepositEditor: React.FC<StabilityDepositEditorProps> = ({
  originalDeposit,
  editedUSDS,
  changePending,
  dispatch,
  children
}) => {
  const { usdsBalance, usdsInStabilityPool } = useLiquitySelector(select);
  const chain = useChain();
  const currencySymbol = chain.nativeCurrency.symbol;

  const editingState = useState<string>();

  const edited = !editedUSDS.eq(originalDeposit.currentUSDS);

  const maxAmount = originalDeposit.currentUSDS.add(usdsBalance);
  const maxedOut = editedUSDS.eq(maxAmount);

  const usdsInStabilityPoolAfterChange = usdsInStabilityPool
    .sub(originalDeposit.currentUSDS)
    .add(editedUSDS);

  const originalPoolShare = originalDeposit.currentUSDS.mulDiv(100, usdsInStabilityPool);
  const newPoolShare = editedUSDS.mulDiv(100, usdsInStabilityPoolAfterChange);
  const poolShareChange =
    originalDeposit.currentUSDS.nonZero &&
    Difference.between(newPoolShare, originalPoolShare).nonZero;

  const { stabilityPoolAPR } = useSPRewardsAPR();

  return (
    <Card>
      <Heading>
        Stability Pool
        {edited && !changePending && (
          <Button
            variant="titleIcon"
            sx={{ ":enabled:hover": { color: "danger" } }}
            onClick={() => dispatch({ type: "revert" })}
          >
            <Icon name="history" size="lg" />
          </Button>
        )}
      </Heading>

      <Box sx={{ p: [2, 3] }}>
        <EditableRow
          label="Deposit"
          trailingLabel={<BalanceLabel balance={usdsBalance.toString()} tokenSymbol={COIN} />}
          inputId="deposit-sable"
          amount={editedUSDS.prettify()}
          maxAmount={maxAmount.toString()}
          maxedOut={maxedOut}
          unit={COIN}
          {...{ editingState }}
          editedAmount={editedUSDS.toString(2)}
          setEditedAmount={newValue => dispatch({ type: "setDeposit", newValue })}
        />

        {newPoolShare.infinite ? (
          <StaticRow label="Pool share" inputId="deposit-share" amount="N/A" />
        ) : (
          <StaticRow
            label="Pool share"
            inputId="deposit-share"
            amount={newPoolShare.prettify(4)}
            pendingAmount={poolShareChange?.prettify(4).concat("%")}
            pendingColor={poolShareChange?.positive ? "success" : "danger"}
            unit="%"
          />
        )}

        {!originalDeposit.isEmpty && (
          <>
            <StaticRow
              label="Liquidation gain"
              inputId="deposit-gain"
              amount={originalDeposit.collateralGain.prettify(4)}
              color={originalDeposit.collateralGain.nonZero && "success"}
              unit={currencySymbol}
            />
            {/** hide sable reward. uncomment it when needed */}
            {/* <StaticRow
              label="Reward"
              inputId="deposit-reward"
              amount={originalDeposit.sableReward.prettify()}
              color={originalDeposit.sableReward.nonZero && "success"}
              unit={GT}
            /> */}
          </>
        )}
        <StaticRow
            label="SABLE APR"
            amount={stabilityPoolAPR}
            color={"black"}
            unit="%"
          />
        {children}
      </Box>

      {changePending && <LoadingOverlay />}
    </Card>
  );
};
